import { Organisation, organisation } from '@futrli-sdk/organisations';

import { API_PATHS, ROUTE_PARAMS } from '../../app-core/constants';
import { interpolatePathParams } from '../../path';
import { fromNormalisedStoreCacheSelector, fromNormalisedStoreSelector, NormalisedData } from '../../redux-helpers';
import { getEnabledDatasources } from '../utils/product-selection-utils';
import { workspaceByIdSelector } from '../../workspaces';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { Consolidation } from '@futrli-sdk/consolidations';

/**
 * Selector to get organisations for a list of workspaceIds.
 *
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string[]} workspaceIds
 * @returns {Organisation[]}
 */
export const organisationsByWorkspacesSelector = (
  normalised: NormalisedData<unknown>,
  workspaceIds: string[]
): Organisation[] | null =>
  workspaceIds.reduce((organisations: Organisation[], workspaceId: string) => {
    const orgForWorkspace = organisationsByWorkspaceIdSelector(normalised, workspaceId);
    if (orgForWorkspace) {
      organisations = organisations.concat(orgForWorkspace);
    }
    return organisations;
  }, []);

/**
 * Selector to get organisations by workspaceId.
 *
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string} workspaceId
 * @returns {Organisation[]}
 */
export const organisationsByWorkspaceIdSelector = (
  normalised: NormalisedData<unknown>,
  workspaceId: string
): Organisation[] | null => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  return fromNormalisedStoreSelector<Organisation[]>(normalised, [organisation], path);
};

/**
 * Selector to get organisations.
 * @deprecated since v0.12.3
 * @see {@link organisationsByWorkspaceIdSelector}
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string} workspaceId
 * @returns {Organisation[]}
 */
export const organisationsSelector = (
  normalised: NormalisedData<unknown>,
  workspaceId: string
): Organisation[] | null => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  return fromNormalisedStoreSelector<Organisation[]>(normalised, [organisation], path);
};

/**
 * Selector to get organisationById.
 *
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string} workspaceId
 * @param {string} orgId
 * @returns {Organisation}
 */
export const organisationByIdSelector = (
  normalised: NormalisedData<unknown>,
  workspaceId: string,
  orgId: string
): Organisation | null => {
  const path = interpolatePathParams(API_PATHS.ORGANISATION_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: orgId
  });

  return fromNormalisedStoreSelector<Organisation>(normalised, organisation, path);
};

/**
 * Selector to pull the first currency from an org's datasources
 *
 * @param {Organisation} organisation
 * @returns {string | undefined}
 */
export const currencySelector = (organisation: Organisation) => {
  const ds = getEnabledDatasources(organisation);
  if (ds?.length > 0) {
    return ds[0]?.currency_code;
  }
};

/**
 * Selector to get organisationById.
 *
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string[]} ids - ids to retrieve.
 * @returns {Organisation}
 */
export const cacheOrganisationByIdSelector = (
  normalised: NormalisedData<unknown>,
  ids: string[]
): Organisation | null =>
  fromNormalisedStoreCacheSelector<Organisation>(normalised, organisation, ids) as Organisation | null;

/**
 * Selector to get organisation ids for a list of workspaceIds.
 *
 * @param {NormalisedData<EntityTypes>} normalised - normalised store
 * @param {string[]} workspaceIds
 */
export const organisationIdsByWorkspacesSelector = (normalised: NormalisedData<unknown>, workspaceIds: string[]) => {
  const data = workspaceIds?.reduce((acc, workspaceId) => {
    const workspace = workspaceByIdSelector(normalised, workspaceId) as WorkspacesListingItem;
    acc[workspaceId] = workspace?.organisations?.map(o => o.id) || [];
    return acc;
  }, {} as Record<string, string[]>);
  return data;
};

/**
 * Selector to get the loaded consolidations (we only load consolidations on a per-workspace basis).
 */
export const consolidationsSelector = (
  normalised: NormalisedData<unknown>
): Record<string, Consolidation> | undefined => {
  return normalised.entities?.Consolidation as Record<string, Consolidation>;
};

/**
 * Selector to get the consolidation data for the given ID
 */
export const consolidationByIdSelector = (
  normalised: NormalisedData<unknown>,
  consolidationId: string
): Consolidation | undefined => {
  return normalised.entities?.Consolidation?.[consolidationId] as Consolidation;
};
